'use client';

import { useCallback } from 'react';

import { useBoolean } from '@chakra-ui/react';
import { Key, useSWRConfig } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { requestFormStoreActions } from '@entities/merchant';

import {
  type GetTags200Result,
  type IndexRequestSchema,
  type ServiceError,
  tagsApi,
} from '@api/services/merchants';

import { getTags } from '../api';

const keyPath = '/marketplaceTags';

const getKey = (requestSchema: IndexRequestSchema): string | null => {
  return `${keyPath}?requestSchema=${JSON.stringify(requestSchema)}`;
};

const fallbackData = {
  count: 0,
  total: 0,
  items: [],
} as Required<NonNullable<GetTags200Result>>;

export const useGetTags = (requestSchema: IndexRequestSchema) => {
  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite<GetTags200Result, ServiceError>(
      (pageIndex, previousPageData) => {
        if (previousPageData && !previousPageData.count) return null;
        return getKey({
          ...requestSchema,
          offset: pageIndex * (requestSchema.limit ?? 0),
        });
      },

      (key: Key) => {
        if (key && typeof key === 'string') {
          const parsedKey = JSON.parse(key.split('?requestSchema=')[1]);
          return getTags(parsedKey);
        }

        return fallbackData;
      },
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
        revalidateFirstPage: false,
        errorRetryCount: 0,
      },
    );

  const items = data ? data.flatMap((page) => page?.items || []) : [];
  const pagesData = data
    ? {
        total: data[0]?.total ?? 0,
        count: items.length,
        items,
      }
    : fallbackData;

  const isEmpty = pagesData.total === 0;
  const isReachedEnd = isEmpty || pagesData.count === pagesData.total;
  const isInitialLoading = size === 1 && isLoading;
  const isLoadingMore =
    !isReachedEnd &&
    size > 1 &&
    (isLoading || (data && typeof data[size - 1] === 'undefined'));

  return {
    data: pagesData,
    error,
    size,
    setSize,
    isEmpty,
    isReachedEnd,
    isLoading,
    isInitialLoading,
    isLoadingMore,
    isValidating,
    mutate,
  };
};

export const useResetTagsCache = () => {
  const { cache } = useSWRConfig();

  const trigger = useCallback(() => {
    for (const key of cache.keys()) {
      if (key.includes(keyPath)) cache.delete(key);
    }
  }, [cache]);

  return { trigger };
};

type MutationData = typeof fallbackData;

interface TriggerBase {
  onSuccess?: () => unknown;
  onError?: () => unknown;
  onComplete?: () => unknown;
}

export const useDeleteTag = (
  tagId: string,
  requestSchema: IndexRequestSchema,
) => {
  const [isLoading, setIsLoading] = useBoolean(false);

  const { setSize } = useGetTags(requestSchema);
  const { mutate, cache } = useSWRConfig();
  const { trigger: resetCache } = useResetTagsCache();

  const updateData = (currentData: MutationData) => ({
    total: currentData.total >= 1 ? currentData.total - 1 : 0,
    count: currentData.count >= 1 ? currentData.count - 1 : 0,
    items: currentData.items.filter((value) => value.id !== tagId),
  });

  const updateCache = useCallback(
    async (key: Key) => {
      await mutate(
        key,
        (keyData: unknown) => {
          const data = keyData as MutationData | MutationData[];

          if (!data) return null;

          if (Array.isArray(data)) {
            const mutateIndex = data.findIndex((item) =>
              item.items?.some((value) => value.id === tagId),
            );

            if (mutateIndex !== -1) {
              const updatedData = [...data];
              const mutationData = data[mutateIndex] as MutationData;
              updatedData[mutateIndex] = updateData(mutationData);

              return updatedData;
            }
          } else {
            return updateData(data);
          }

          return data;
        },
        { revalidate: false },
      );
    },
    [tagId, mutate],
  );

  const trigger = useCallback(
    async (params: TriggerBase): Promise<void> => {
      try {
        setIsLoading.on();
        await tagsApi.deleteTag(tagId);

        for (const key of cache.keys()) {
          if (key.includes(keyPath)) {
            await updateCache(key);
          }
        }
        requestFormStoreActions.resetMarketplaceTags();
        params?.onSuccess?.();
      } catch {
        resetCache();
        setSize(1);
        params?.onError?.();
      } finally {
        setIsLoading.off();
        params?.onComplete?.();
      }
    },
    [updateCache, tagId],
  );

  return { isLoading, trigger };
};
